/** Registry of the three modules — drives the company-hub tiles, header
 *  badges, and workspace banners. UI-only concern; the backend has its own
 *  registry (module_registry.py) for the schema-driven modules. */

import { FTR_SECTIONS } from './schema/ftrSchema'
import { WAFR_SECTIONS } from './schema/wafrSchema'
import { paths, type ModuleKey } from './routes'
import type { SectionDef } from './schema/types'

export interface ModuleDef {
  key: ModuleKey
  label: string
  /** Compact badge label (the company list uses acronyms: WAFR, FTR). */
  shortLabel: string
  /** Short tile blurb on the company hub. */
  description: string
  /** CSS class suffix for the module accent tokens (--module-<badge>-…). */
  badge: 'ai' | 'wafr' | 'ftr'
  home: (slug: string) => string
  /** Questionnaire sections (schema-driven modules only; AI has its own flow). */
  sections: SectionDef[] | null
  /** The questionnaire page carries a Submission station (`?section=_submission`). */
  hasSubmission: boolean
}

export const MODULES: Record<ModuleKey, ModuleDef> = {
  'ai-assessment': {
    key: 'ai-assessment',
    label: 'AI Assessment',
    shortLabel: 'AI Assessment',
    description:
      'AWS AI Assessment Funding engagement — intake, agent proposals, ROI, and the submission deliverables.',
    badge: 'ai',
    home: (slug) => paths.ai(slug),
    sections: null,
    hasSubmission: false,
  },
  wafr: {
    key: 'wafr',
    label: 'Well-Architected Review',
    shortLabel: 'WAFR',
    description:
      'Playbook-driven Well-Architected review: evidence sweep, guided sessions, risk register.',
    badge: 'wafr',
    home: (slug) => paths.wafr(slug),
    sections: WAFR_SECTIONS,
    hasSubmission: false,
  },
  ftr: {
    key: 'ftr',
    label: 'Foundational Technical Review',
    shortLabel: 'FTR',
    description:
      'FTR readiness — the five eligibility prerequisites, the submission pathway (SOC 2 or WAFR), and the recorded outcome.',
    badge: 'ftr',
    home: (slug) => paths.ftr(slug),
    sections: FTR_SECTIONS,
    hasSubmission: true,
  },
}

export const MODULE_KEYS: ModuleKey[] = ['ai-assessment', 'wafr', 'ftr']

/** The `module_type` strings the companies API returns. */
export function moduleFromApi(moduleType: string): ModuleKey | null {
  return (MODULE_KEYS as string[]).includes(moduleType) ? (moduleType as ModuleKey) : null
}
